import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, WritableSignal, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SvgComponent } from '../shared/svg/svg.component';
import { SettingsService } from '@app/_services/settings.service';
import {
  getSummary,
  getTopOperations,
  getFirestoreByCollection,
  getStorageBreakdown,
  getRtdbBreakdown,
  getFunctionBreakdown,
  getRtdbChangesByCollection,
  resetSession,
  formatBytes,
  formatCost,
  formatDuration,
  InfrastructureSummary,
  RankedOperation,
  FirestoreCollectionBreakdown,
  StorageBreakdown,
  FunctionBreakdown,
  RtdbFeatureBreakdown,
  RtdbCollectionChanges,
} from '@app/_services/cost-tracker';

type DebugTab = 'costs';
type CostView = 'summary' | 'firestore' | 'storage' | 'rtdb' | 'functions';

@Component({
  selector: 'app-debug-screen',
  imports: [
    CommonModule,
    SvgComponent,
  ],
  templateUrl: './debug-screen.component.html',
  styleUrl: './debug-screen.component.scss'
})

export class DebugScreenComponent implements OnDestroy {

  // Tab navigation
  activeTab: WritableSignal<DebugTab> = signal<DebugTab>('costs');
  costView: WritableSignal<CostView> = signal<CostView>('summary');

  // Cost data (refreshed on tab/view change)
  summary: WritableSignal<InfrastructureSummary | null> = signal(null);
  topOperations: WritableSignal<RankedOperation[]> = signal([]);
  firestoreBreakdown: WritableSignal<FirestoreCollectionBreakdown[]> = signal([]);
  storageBreakdown: WritableSignal<StorageBreakdown | null> = signal(null);
  rtdbBreakdown: WritableSignal<Record<string, RtdbFeatureBreakdown>> = signal({});
  rtdbChanges: WritableSignal<RtdbCollectionChanges[]> = signal([]);
  functionBreakdown: WritableSignal<FunctionBreakdown[]> = signal([]);

  // Expanded state for collapsible sections
  expandedCollections: WritableSignal<Set<string>> = signal(new Set());
  expandedFunctions: WritableSignal<Set<string>> = signal(new Set());

  // Real-time update interval (refreshes every 2 seconds when on costs tab)
  private refreshInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    public dialogRef: MatDialogRef<DebugScreenComponent>,
    public ss: SettingsService,
    private cdr: ChangeDetectorRef,
    private appRef: ApplicationRef,
  ) {
    // Load initial cost data
    this.refreshCostData();
    // Start real-time updates for costs tab
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  private startAutoRefresh(): void {
    // Refresh every 2 seconds when viewing costs
    this.refreshInterval = setInterval(() => {
      if (this.activeTab() === 'costs') {
        this.refreshCostData();
      }
    }, 2000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  // Tab navigation
  setActiveTab(tab: DebugTab) {
    this.activeTab.set(tab);
    if (tab === 'costs') {
      this.refreshCostData();
    }
  }

  setCostView(view: CostView) {
    this.costView.set(view);
    this.refreshCostData();
  }

  goBack() {
    this.costView.set('summary');
    this.refreshCostData();
  }

  // Refresh cost data based on current view
  refreshCostData() {
    this.summary.set(getSummary());
    this.topOperations.set(getTopOperations(5));

    const view = this.costView();
    if (view === 'firestore') {
      this.firestoreBreakdown.set(getFirestoreByCollection());
    } else if (view === 'storage') {
      this.storageBreakdown.set(getStorageBreakdown());
    } else if (view === 'rtdb') {
      this.rtdbBreakdown.set(getRtdbBreakdown());
      this.rtdbChanges.set(getRtdbChangesByCollection());
    } else if (view === 'functions') {
      this.functionBreakdown.set(getFunctionBreakdown());
    }
  }

  resetCostSession() {
    resetSession();
    this.refreshCostData();
  }

  // Toggle expanded state
  toggleCollection(collection: string) {
    const expanded = new Set(this.expandedCollections());
    if (expanded.has(collection)) {
      expanded.delete(collection);
    } else {
      expanded.add(collection);
    }
    this.expandedCollections.set(expanded);
  }

  toggleFunction(fnName: string) {
    const expanded = new Set(this.expandedFunctions());
    if (expanded.has(fnName)) {
      expanded.delete(fnName);
    } else {
      expanded.add(fnName);
    }
    this.expandedFunctions.set(expanded);
  }

  isCollectionExpanded(collection: string): boolean {
    return this.expandedCollections().has(collection);
  }

  isFunctionExpanded(fnName: string): boolean {
    return this.expandedFunctions().has(fnName);
  }

  // Formatting helpers
  formatBytes(bytes: number): string {
    return formatBytes(bytes);
  }

  formatCost(cost: number): string {
    return formatCost(cost);
  }

  formatDuration(ms: number): string {
    return formatDuration(ms);
  }

  formatSessionTime(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  // Get storage entries as arrays for iteration
  getStorageUploads(): { feature: string; count: number; bytes: number }[] {
    const breakdown = this.storageBreakdown();
    if (!breakdown) return [];
    return Object.entries(breakdown.uploads).map(([feature, m]) => ({
      feature,
      count: m.count,
      bytes: m.bytes,
    }));
  }

  getStorageDownloads(): { feature: string; count: number; bytes: number }[] {
    const breakdown = this.storageBreakdown();
    if (!breakdown) return [];
    return Object.entries(breakdown.downloads).map(([feature, m]) => ({
      feature,
      count: m.count,
      bytes: m.bytes,
    }));
  }

  getRtdbFeatures(): { feature: string; data: RtdbFeatureBreakdown }[] {
    const breakdown = this.rtdbBreakdown();
    return Object.entries(breakdown).map(([feature, data]) => ({ feature, data }));
  }

  closeDialog() {
    // Trigger one final change detection before closing to ensure all components update
    this.appRef.tick();
    this.dialogRef.close();
  }
}
